import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { format } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from '../../components/ConfirmationModal';
import { fetchEvent, updateEvent, deleteEvent } from '../../hooks/calendarApi';
import { getEmployees } from '../../hooks/employees';
import { CalendarEvent, Employee } from '../../utils/types';
import {
  CalendarField,
  CalendarHero,
  CalendarPrimaryButton,
  CalendarSecondaryButton,
  CalendarSectionCard,
  CalendarShell,
  CalendarSkeleton,
  CalendarStatusBadge,
  CalendarTypeBadge,
  formatCalendarDateTime,
} from './components/calendarUi';

type EventFormValues = {
  title: string;
  description: string;
  start_datetime: string;
  end_datetime: string;
  status: CalendarEvent['status'];
  assigned_to: string;
  event_type: CalendarEvent['event_type'];
};

const toLocalInputValue = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, "yyyy-MM-dd'T'HH:mm");
};

const mapEventToFormValues = (event: CalendarEvent): EventFormValues => ({
  title: event.title || '',
  description: event.description || '',
  start_datetime: toLocalInputValue(event.start_datetime),
  end_datetime: toLocalInputValue(event.end_datetime),
  status: event.status || 'upcoming',
  assigned_to: event.assigned_to || '',
  event_type: event.event_type || 'custom',
});

export default function EditEventPage() {
  const { orgId, eventId } = useParams<{ orgId: string; eventId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const formRef = useRef<HTMLFormElement | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [formData, setFormData] = useState<EventFormValues | null>(null);
  const [originalData, setOriginalData] = useState<EventFormValues | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !eventId) {
        return;
      }

      setLoading(true);
      try {
        const [eventResponse, employeesResponse] = await Promise.all([
          fetchEvent(orgId, eventId),
          getEmployees(orgId),
        ]);

        const nextEvent = eventResponse.data;
        const nextFormData = mapEventToFormValues(nextEvent);
        setEvent(nextEvent);
        setFormData(nextFormData);
        setOriginalData(nextFormData);

        if (employeesResponse?.success && Array.isArray(employeesResponse.data)) {
          setEmployees(employeesResponse.data);
        } else {
          setEmployees([]);
        }
      } catch {
        toast({ title: 'Failed to load event', status: 'error' });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [eventId, orgId, toast]);

  const linkedSourceLabel = event?.related_task
    ? 'task'
    : event?.related_lead
      ? 'lead follow-up'
      : null;

  const changedCount = useMemo(() => {
    if (!formData || !originalData) {
      return 0;
    }

    return Object.entries(formData).filter(
      ([key, value]) => originalData[key as keyof EventFormValues] !== value,
    ).length;
  }, [formData, originalData]);

  const handleChange = (
    eventInput: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = eventInput.target;
    setFormData((current) => (current ? { ...current, [name]: value } : current));
  };

  const handleSubmit = async (eventInput: React.FormEvent) => {
    eventInput.preventDefault();

    if (!orgId || !eventId || !formData || !originalData) {
      return;
    }

    if (!formData.title.trim() || !formData.start_datetime) {
      toast({ title: 'Title and start time are required', status: 'warning' });
      return;
    }

    if (formData.end_datetime && new Date(formData.end_datetime) <= new Date(formData.start_datetime)) {
      toast({ title: 'End time must be after start time', status: 'warning' });
      return;
    }

    const changed: Partial<CalendarEvent> = {};

    if (formData.title !== originalData.title) changed.title = formData.title;
    if (formData.description !== originalData.description) changed.description = formData.description;
    if (formData.status !== originalData.status) changed.status = formData.status;
    if (formData.assigned_to !== originalData.assigned_to) changed.assigned_to = formData.assigned_to || undefined;
    if (!linkedSourceLabel && formData.event_type !== originalData.event_type) {
      changed.event_type = formData.event_type;
    }
    if (formData.start_datetime !== originalData.start_datetime) {
      changed.start_datetime = new Date(formData.start_datetime).toISOString();
    }
    if (formData.end_datetime !== originalData.end_datetime) {
      changed.end_datetime = formData.end_datetime ? new Date(formData.end_datetime).toISOString() : undefined;
    }

    if (Object.keys(changed).length === 0) {
      toast({ title: 'No changes to save', status: 'info' });
      return;
    }

    setSaving(true);
    try {
      await updateEvent(orgId, eventId, changed);
      toast({ title: 'Event updated', status: 'success' });
      navigate(`/organization/${orgId}/events/${eventId}/view`);
    } catch {
      toast({ title: 'Failed to update event', status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!orgId || !eventId) {
      return;
    }

    setDeleting(true);
    try {
      await deleteEvent(orgId, eventId);
      toast({ title: 'Event deleted', status: 'success' });
      navigate(`/organization/${orgId}/calendar`);
    } catch {
      toast({ title: 'Failed to delete event', status: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <CalendarShell compact>
        <CalendarSkeleton />
      </CalendarShell>
    );
  }

  if (!event || !formData) {
    return (
      <CalendarShell compact>
        <CalendarSectionCard title="Event not found" description="The requested event could not be loaded.">
          <CalendarSecondaryButton onClick={() => navigate(`/organization/${orgId}/calendar`)}>
            Back to calendar
          </CalendarSecondaryButton>
        </CalendarSectionCard>
      </CalendarShell>
    );
  }

  return (
    <CalendarShell compact>
      <CalendarHero
        eyebrow="Edit event"
        title={event.title || 'Edit event'}
        description={
          linkedSourceLabel
            ? `This calendar item is synced from a ${linkedSourceLabel}. Changes here can be overwritten when the source record changes.`
            : 'Update timing, ownership, and state while keeping the event context visible.'
        }
        primaryAction={
          <CalendarPrimaryButton
            leftIcon={<EditIcon />}
            onClick={() => formRef.current?.requestSubmit()}
            isLoading={saving}
          >
            Save changes
          </CalendarPrimaryButton>
        }
        secondaryAction={
          <CalendarSecondaryButton onClick={() => navigate(`/organization/${orgId}/events/${eventId}/view`)}>
            Cancel
          </CalendarSecondaryButton>
        }
      />

      <form ref={formRef} onSubmit={handleSubmit}>
        <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
          <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
            <CalendarSectionCard title="Basics" description="The core event information people see first.">
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Title</FormLabel>
                  <Input name="title" value={formData.title} onChange={handleChange} borderRadius="16px" bg="gray.50" />
                </FormControl>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    minH="140px"
                    resize="vertical"
                    borderRadius="16px"
                    bg="gray.50"
                  />
                </FormControl>
              </Stack>
            </CalendarSectionCard>

            <CalendarSectionCard title="Schedule and ownership" description="Set the exact timing and the employee responsible for the event.">
              <Stack spacing={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Start date and time</FormLabel>
                    <Input
                      type="datetime-local"
                      name="start_datetime"
                      value={formData.start_datetime}
                      onChange={handleChange}
                      borderRadius="16px"
                      bg="gray.50"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>End date and time</FormLabel>
                    <Input
                      type="datetime-local"
                      name="end_datetime"
                      value={formData.end_datetime}
                      onChange={handleChange}
                      borderRadius="16px"
                      bg="gray.50"
                    />
                    <FormHelperText>Leave empty only if the event does not need a distinct end.</FormHelperText>
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>Assigned to</FormLabel>
                    <Select
                      name="assigned_to"
                      value={formData.assigned_to}
                      onChange={handleChange}
                      placeholder="No assignee"
                      borderRadius="16px"
                      bg="gray.50"
                    >
                      {employees.map((employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.user?.first_name} {employee.user?.last_name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Status</FormLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      borderRadius="16px"
                      bg="gray.50"
                    >
                      <option value="upcoming">Upcoming</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </Select>
                  </FormControl>
                </SimpleGrid>

                <FormControl isDisabled={Boolean(linkedSourceLabel)}>
                  <FormLabel>Event type</FormLabel>
                  <Select
                    name="event_type"
                    value={formData.event_type}
                    onChange={handleChange}
                    borderRadius="16px"
                    bg="gray.50"
                  >
                    <option value="task">Task</option>
                    <option value="lead_followup">Lead follow-up</option>
                    <option value="meeting">Meeting</option>
                    <option value="deadline">Deadline</option>
                    <option value="custom">Custom</option>
                  </Select>
                  {linkedSourceLabel ? (
                    <FormHelperText>This event type is controlled by the linked source record.</FormHelperText>
                  ) : null}
                </FormControl>
              </Stack>
            </CalendarSectionCard>
          </Stack>

          <VStack align="stretch" spacing={6}>
            <CalendarSectionCard title="Event state" description="A quick read of what will change when you save.">
              <Stack spacing={4}>
                <HStack justify="space-between">
                  <Text color="gray.600">Type</Text>
                  <CalendarTypeBadge type={formData.event_type} />
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Status</Text>
                  <CalendarStatusBadge status={formData.status} />
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Changed fields</Text>
                  <Text fontWeight="800" color="gray.900">{changedCount}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Assignee</Text>
                  <Text fontWeight="700" color="gray.900">
                    {employees.find((employee) => employee.id === formData.assigned_to)
                      ? `${employees.find((employee) => employee.id === formData.assigned_to)?.user?.first_name || ''} ${employees.find((employee) => employee.id === formData.assigned_to)?.user?.last_name || ''}`.trim()
                      : 'Unassigned'}
                  </Text>
                </HStack>
              </Stack>
            </CalendarSectionCard>

            <CalendarSectionCard title="Context" description="Reference details from the current event record.">
              <Stack spacing={4}>
                <CalendarField label="Starts" value={formatCalendarDateTime(event.start_datetime)} />
                <CalendarField label="Ends" value={formatCalendarDateTime(event.end_datetime)} />
                <CalendarField label="Created at" value={formatCalendarDateTime(event.created_at)} />
                <CalendarField label="Updated at" value={formatCalendarDateTime(event.updated_at)} />
              </Stack>
            </CalendarSectionCard>

            <CalendarSectionCard title="Linked source" description="Where this event came from and where to manage it if needed.">
              <Stack spacing={4}>
                {event.related_task ? (
                  <Button
                    variant="outline"
                    borderRadius="16px"
                    onClick={() => navigate(`/organization/${orgId}/tasks/${event.related_task}/view`)}
                  >
                    Open linked task
                  </Button>
                ) : null}
                {event.related_lead ? (
                  <Button
                    variant="outline"
                    borderRadius="16px"
                    onClick={() => navigate(`/organization/${orgId}/leads/${event.related_lead}`)}
                  >
                    Open linked lead
                  </Button>
                ) : null}
                {!event.related_task && !event.related_lead ? (
                  <Text fontSize="sm" color="gray.500">
                    This event is not linked to a task or lead.
                  </Text>
                ) : null}
              </Stack>
            </CalendarSectionCard>

            {!event.related_task && !event.related_lead ? (
              <CalendarSectionCard title="Danger zone" description="Delete only if this calendar item should be removed permanently.">
                <Button
                  leftIcon={<DeleteIcon />}
                  variant="outline"
                  colorScheme="red"
                  borderRadius="16px"
                  onClick={onOpen}
                  isLoading={deleting}
                >
                  Delete event
                </Button>
              </CalendarSectionCard>
            ) : null}
          </VStack>
        </SimpleGrid>
      </form>

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        handleConfirm={handleDelete}
        isLoading={deleting}
        AreYouSureQuestion="Delete event?"
        AreYouSureTextExplanation={`This will permanently remove ${event.title}.`}
        confirmButtonText="Delete event"
        confirmButtonColor="#DC2626"
        colorScheme="red"
      />
    </CalendarShell>
  );
}
